import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { getServerMastersList, saveServerMaster, sendSmsTest, provisionApplicationSettings } from '../../utilities/api';
import '../CategoryMaster.css';
import '../../custom.css';

const STATUS_OPTIONS = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
];

const ACCESS_OPTIONS = [
    { label: '0', value: 0 },
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 }
];

const DATA_TRANSFER_OPTIONS = [
    { label: 'Updated', value: 'updated' },
    { label: 'Pending', value: 'pending' }
];

const STATUS_FIELD_MAP = {
    client_creation: 'status',
    branch_creation: 'branch_status',
    setting: 'status',
    application_setting: 'setting_status',
    masters_creation: 'status',
    users: 'user_status',
    sms_authorize_final: 'status',
    tables: 'table_status'
};

const FIELD_VALIDATION_RULES = {
    client_creation: {
        client_name: { required: true, maxLength: 255, label: 'Client Name' },
        no_of_branches: { required: true, type: 'number', label: 'No Of Branches' },
        branch_prefix: { required: true, maxLength: 50, label: 'Branch Prefix' },
        status: { required: true, label: 'Status' },
        dayend_mailpdf: { required: false, type: 'number', label: 'Dayend State' },
        short_url: { required: false, maxLength: 500, type: 'url', label: 'Short URL' },
        report_url: { required: false, maxLength: 500, type: 'url', label: 'Report URL' },
        base_url: { required: true, maxLength: 500, type: 'url', label: 'Base URL' },
        server_url: { required: false, maxLength: 500, type: 'url', label: 'Server URL' }
    },
    branch_creation: {
        client_id: { required: true, label: 'Client' },
        branch_name: { required: true, maxLength: 255, label: 'Branch Name' },
        branch_title: { required: false, maxLength: 255, label: 'Branch Title' },
        branch_slno: { required: true, maxLength: 50, label: 'Branch Serial No' },
        invoice_prefix: { required: true, maxLength: 50, label: 'Invoice Prefix' },
        org_name: { required: false, maxLength: 255, label: 'Organization' },
        land_no: { required: false, maxLength: 20, type: 'mobile', label: 'Land Phone' },
        mobile: { required: false, maxLength: 15, type: 'mobile', label: 'Mobile No' },
        email_id: { required: false, maxLength: 255, type: 'email', label: 'Email' },
        gstin_no: { required: false, maxLength: 15, label: 'GSTIN' },
        address1: { required: true, maxLength: 500, label: 'Address 1' },
        address2: { required: false, maxLength: 500, label: 'Address 2' },
        website_name: { required: false, maxLength: 255, type: 'url', label: 'Website' },
        cin_no: { required: false, maxLength: 50, label: 'CIN Number' },
        pincode: { required: false, maxLength: 10, label: 'Pincode' },
        location: { required: false, maxLength: 255, label: 'Location' },
        social_order_percentage: { required: false, type: 'number', label: 'Social Order %' },
        branch_status: { required: true, label: 'Status' },
        effective_from: { required: true, type: 'date', label: 'Effective From' }
    },
    setting: {
        setting_type: { required: true, maxLength: 100, label: 'Setting Type' },
        setting_name: { required: true, maxLength: 255, label: 'Setting Name' },
        setting_title: { required: true, maxLength: 255, label: 'Setting Title' },
        status: { required: true, label: 'Status' }
    },
    application_setting: {
        client_id: { required: true, label: 'Client' },
        branch_id: { required: true, label: 'Branch' },
        setting_type_master: { required: true, label: 'Setting Type' },
        setting_type: { required: true, maxLength: 100, label: 'Setting Type (Custom)' },
        setting_name: { required: true, maxLength: 255, label: 'Setting Name' },
        platform_id: { required: true, label: 'Platform' },
        setting_title: { required: true, maxLength: 255, label: 'Setting Title' },
        setting_access: { required: true, label: 'Setting Access' },
        effective_from: { required: true, type: 'date', label: 'Effective From' },
        setting_status: { required: true, label: 'Status' }
    },
    masters_creation: {
        client_id: { required: true, label: 'Client' },
        branch_id: { required: true, label: 'Branch' },
        master_id: { required: true, label: 'Master' },
        platform_id: { required: true, label: 'Platform' },
        master_type: { required: true, maxLength: 255, label: 'Master Name' },
        master_details: { required: true, maxLength: 255, label: 'Master Code' },
        master_image_path: { required: false, maxLength: 255, label: 'Master Image Path' },
        effective_from: { required: true, type: 'date', label: 'Effective From' },
        status: { required: true, label: 'Status' }
    },
    users: {
        client_id: { required: true, label: 'Client' },
        branch_id: { required: false, label: 'Branch' },
        username: { required: true, maxLength: 255, label: 'Username' },
        password: { required: true, minLength: 6, maxLength: 255, label: 'Password' },
        re_password: { required: false, type: 'confirm_password', label: 'Confirm Password' },
        mobile: { required: false, maxLength: 15, type: 'mobile', label: 'Mobile' },
        email_id: { required: false, maxLength: 255, type: 'email', label: 'Email' },
        platform_id: { required: true, label: 'Platform' },
        roleId: { required: true, label: 'Role' },
        user_status: { required: true, label: 'Status' },
        effective_from: { required: true, type: 'date', label: 'Effective From' }
    },
    sms_authorize_final: {
        client_id: { required: true, label: 'Client' },
        branch_id: { required: true, label: 'Branch' },
        message_type_id: { required: true, label: 'Message Type' },
        sender_id: { required: true, maxLength: 255, label: 'Sender ID' },
        enitity_id: { required: true, maxLength: 255, label: 'Entity ID' },
        api_key: { required: true, maxLength: 1000, label: 'API Key' },
        template_id: { required: true, maxLength: 255, label: 'Template ID' },
        user_id: { required: true, maxLength: 255, label: 'User ID' },
        mobile_no: { required: true, type: 'mobile', maxLength: 15, label: 'Mobile Number' },
        owner_num: { required: false, type: 'mobile', maxLength: 15, label: 'Owner Number' },
        branch_title: { required: false, maxLength: 255, label: 'Branch Title' },
        location: { required: false, maxLength: 255, label: 'Location' },
        email_id: { required: false, type: 'email', maxLength: 255, label: 'Email' },
        adminstrative_email: { required: false, type: 'email', maxLength: 255, label: 'Admin Email' },
        shorts_url: { required: false, type: 'url', label: 'Short URL' },
        mobileno_testing: { required: false, type: 'mobile', maxLength: 15, label: 'Testing Mobile No' },
        status: { required: true, label: 'Status' }
    },
    tables: {
        table_name: { required: true, maxLength: 255, label: 'Table Name' },
        seating_capacity: { required: true, type: 'number', label: 'Seating Capacity' },
        section_id: { required: true, label: 'Section' },
        supplier_id: { required: true, label: 'Supplier' },
        table_status: { required: true, label: 'Status' }
    }
};

const UNIQUE_FIELD_COMBINATIONS = {
    client_creation: [['client_name']],
    branch_creation: [['branch_name', 'client_id']],
    setting: [['setting_name', 'setting_type']],
    application_setting: [['setting_name', 'client_id', 'branch_id']],
    masters_creation: [['master_details', 'client_id', 'branch_id']],
    users: [['username', 'client_id', 'branch_id']],
    sms_authorize_final: [['sender_id', 'client_id', 'branch_id']],
    tables: [['table_name']]
};

const TABLE_CONFIGS = {
    client_creation: {
        pk: 'client_id',
        label: 'Clients',
        icon: 'pi pi-building',
        visibleCols: ['client_name', 'no_of_branches', 'branch_prefix', 'status', 'dayend_mailpdf', 'short_url'],
        formFields: [
            { key: 'client_name', label: 'Client Name', type: 'text', required: true },
            { key: 'no_of_branches', label: 'No Of Branches', type: 'text', required: true },
            { key: 'branch_prefix', label: 'Branch Prefix', type: 'text', required: true },
            { key: 'status', label: 'Status', type: 'status', required: true },
            { key: 'dayend_mailpdf', label: 'Dayend State', type: 'text', required: false },
            { key: 'short_url', label: 'Short URL', type: 'url', required: false },
            { key: 'report_url', label: 'Report URL', type: 'url', required: false },
            { key: 'base_url', label: 'Base URL', type: 'url', required: true },
            { key: 'server_url', label: 'Server URL', type: 'url', required: false }
        ],
        hasFilters: false
    },
    branch_creation: {
        pk: 'branch_id',
        label: 'Branches',
        icon: 'pi pi-map-marker',
        visibleCols: ['client_id', 'branch_name', 'branch_slno', 'invoice_prefix', 'mobile', 'gstin_no', 'branch_status', 'effective_from', 'data_transfer', 'config_status', 'config_date'],
        formFields: [
            { key: 'client_id', label: 'Client', type: 'client', required: true },
            { key: 'branch_name', label: 'Branch Name', type: 'text', required: true },
            { key: 'branch_title', label: 'Branch Title', type: 'text', required: false },
            { key: 'branch_slno', label: 'Branch Serial No', type: 'text', required: true },
            { key: 'invoice_prefix', label: 'Invoice Prefix', type: 'text', required: true },
            { key: 'org_name', label: 'Organization', type: 'text', required: false },
            { key: 'land_no', label: 'Land Phone', type: 'text', required: false },
            { key: 'mobile', label: 'Mobile No', type: 'text', required: false },
            { key: 'email_id', label: 'Email', type: 'email', required: false },
            { key: 'gstin_no', label: 'GSTIN', type: 'text', required: false },
            { key: 'address1', label: 'Address 1', type: 'text', required: true },
            { key: 'address2', label: 'Address 2', type: 'text', required: false },
            { key: 'website_name', label: 'Website', type: 'text', required: false },
            { key: 'cin_no', label: 'CIN Number', type: 'text', required: false },
            { key: 'pincode', label: 'Pincode', type: 'text', required: false },
            { key: 'location', label: 'Location', type: 'text', required: false },
            { key: 'social_order_percentage', label: 'Social Order %', type: 'text', required: false },
            { key: 'branch_status', label: 'Status', type: 'status', required: true },
            { key: 'effective_from', label: 'Effective From', type: 'date', required: true }
        ],
        hasFilters: true,
        filterFields: ['client']
    },
    setting: {
        pk: 'setting_id',
        label: 'Setting Master',
        icon: 'pi pi-sliders-h',
        visibleCols: ['setting_type', 'setting_name', 'setting_title', 'status'],
        formFields: [
            { key: 'setting_type', label: 'Setting Type', type: 'text', required: true },
            { key: 'setting_name', label: 'Setting Name', type: 'text', required: true },
            { key: 'setting_title', label: 'Setting Title', type: 'text', required: true },
            { key: 'status', label: 'Status', type: 'status', required: true }
        ],
        hasFilters: false
    },
    application_setting: {
        pk: 'app_setting_id',
        label: 'Application Settings',
        icon: 'pi pi-cog',
        visibleCols: ['client_id', 'branch_id', 'setting_name', 'setting_type', 'setting_title', 'setting_access', 'setting_status', 'effective_from', 'data_transfer'],
        formFields: [
            { key: 'client_id', label: 'Client', type: 'client', required: true },
            { key: 'branch_id', label: 'Branch', type: 'branch', required: true },
            { key: 'setting_type_master', label: 'Setting Type', type: 'setting_type', required: true },
            { key: 'setting_type', label: 'Setting Type (Custom)', type: 'setting_type_custom', required: true },
            { key: 'setting_name', label: 'Setting Name', type: 'setting_name', required: true },
            { key: 'platform_id', label: 'Platform', type: 'platform', required: true },
            { key: 'setting_title', label: 'Setting Title', type: 'text', required: true },
            { key: 'setting_access', label: 'Setting Access', type: 'access', required: true },
            { key: 'effective_from', label: 'Effective From', type: 'date', required: true },
            { key: 'setting_status', label: 'Status', type: 'status', required: true }
        ],
        hasFilters: true,
        filterFields: ['client', 'branch'],
        hasRowSelection: true,
        hasCellEdit: true
    },
    masters_creation: {
        pk: 'masterId',
        label: 'Masters Creation',
        icon: 'pi pi-database',
        visibleCols: ['client_id', 'branch_id', 'master_type', 'master_details', 'status', 'effective_from', 'data_transfer'],
        formFields: [
            { key: 'client_id', label: 'Client', type: 'client', required: true },
            { key: 'branch_id', label: 'Branch', type: 'branch', required: true },
            { key: 'master_id', label: 'Master', type: 'master', required: true },
            { key: 'platform_id', label: 'Platform', type: 'platform', required: true },
            { key: 'master_type', label: 'Master Name', type: 'text', required: true },
            { key: 'master_details', label: 'Master Code', type: 'text', required: true },
            { key: 'master_image_path', label: 'Master Image Path', type: 'text', required: false },
            { key: 'effective_from', label: 'Effective From', type: 'date', required: true },
            { key: 'status', label: 'Status', type: 'status', required: true }
        ],
        hasFilters: true,
        filterFields: ['client', 'branch'],
        hasRowSelection: true
    },
    users: {
        pk: 'userId',
        label: 'Users',
        icon: 'pi pi-users',
        visibleCols: ['client_id', 'branch_id', 'username', 'mobile', 'email_id', 'roleId', 'user_status', 'effective_from', 'data_transfer'],
        formFields: [
            { key: 'client_id', label: 'Client', type: 'client', required: true },
            { key: 'branch_id', label: 'Branch', type: 'branch', required: false },
            { key: 'platform_id', label: 'Platform', type: 'platform', required: true },
            { key: 'roleId', label: 'Role', type: 'role', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 're_password', label: 'Confirm Password', type: 'confirm_password', required: false },
            { key: 'mobile', label: 'Mobile', type: 'text', required: false },
            { key: 'email_id', label: 'Email', type: 'email', required: false },
            { key: 'user_status', label: 'Status', type: 'status', required: true },
            { key: 'effective_from', label: 'Effective From', type: 'date', required: true }
        ],
        hasFilters: true,
        filterFields: ['client', 'branch']
    },
    sms_authorize_final: {
        pk: 'auth_id',
        label: 'SMS Configuration',
        icon: 'pi pi-send',
        visibleCols: ['client_id', 'branch_id', 'message_type_id', 'sender_id', 'enitity_id', 'api_key', 'template_id', 'mobile_no', 'status'],
        formFields: [
            { key: 'client_id', label: 'Client', type: 'client', required: true },
            { key: 'branch_id', label: 'Branch', type: 'branch', required: true },
            { key: 'message_type_id', label: 'Message Type', type: 'message_type', required: true },
            { key: 'sender_id', label: 'Sender ID', type: 'text', required: true },
            { key: 'enitity_id', label: 'Entity ID', type: 'text', required: true },
            { key: 'api_key', label: 'API Key', type: 'text', required: true },
            { key: 'template_id', label: 'Template ID', type: 'text', required: true },
            { key: 'user_id', label: 'User ID', type: 'text', required: true },
            { key: 'mobile_no', label: 'Mobile Number', type: 'text', required: true },
            { key: 'owner_num', label: 'Owner Number', type: 'text', required: false },
            { key: 'branch_title', label: 'Branch Title', type: 'text', required: false },
            { key: 'location', label: 'Location', type: 'text', required: false },
            { key: 'email_id', label: 'Email', type: 'email', required: false },
            { key: 'adminstrative_email', label: 'Admin Email', type: 'email', required: false },
            { key: 'shorts_url', label: 'Short URL', type: 'url', required: false },
            { key: 'mobileno_testing', label: 'Testing Mobile No', type: 'text', required: false },
            { key: 'status', label: 'Status', type: 'status', required: true }
        ],
        hasFilters: true,
        filterFields: ['client', 'branch', 'message_type'],
        hasSmsTest: true
    },
    tables: {
        pk: 'table_id',
        label: 'Tables',
        icon: 'pi pi-table',
        visibleCols: ['table_name', 'seating_capacity', 'section_id', 'supplier_id', 'table_status'],
        formFields: [
            { key: 'section_id', label: 'Section', type: 'section', required: true },
            { key: 'supplier_id', label: 'Supplier', type: 'supplier', required: true },
            { key: 'table_name', label: 'Table Name', type: 'text', required: true },
            { key: 'seating_capacity', label: 'Seating Capacity', type: 'text', required: true },
            { key: 'table_status', label: 'Status', type: 'status', required: true }
        ],
        hasFilters: false
    }
};

const HEADER_MAP = {
    client_id: 'Client',
    branch_id: 'Branch',
    user_status: 'Status',
    branch_status: 'Status',
    master_status: 'Status',
    setting_status: 'Status',
    table_status: 'Status',
    effective_from: 'Effective From',
    branch_name: 'Branch Name',
    branch_title: 'Branch Title',
    branch_slno: 'Sl No.',
    invoice_prefix: 'Invoice Prefix',
    org_name: 'Organization',
    land_no: 'Land Phone',
    email_id: 'Email',
    gstin_no: 'GSTIN',
    address1: 'Address 1',
    address2: 'Address 2',
    website_name: 'Website',
    cin_no: 'CIN Number',
    pincode: 'Pincode',
    location: 'Location',
    social_order_percentage: 'Social Order %',
    config_status: 'Config Status',
    config_date: 'Config Date',
    client_name: 'Client Name',
    no_of_branches: 'No Of Branches',
    branch_prefix: 'Branch Prefix',
    dayend_mailpdf: 'Dayend State',
    short_url: 'Short URL',
    report_url: 'Report URL',
    base_url: 'Base URL',
    server_url: 'Server URL',
    roleId: 'Role',
    re_password: 'Confirm Password',
    platform_id: 'Platform',
    setting_name: 'Setting Name',
    setting_type: 'Setting Type',
    setting_title: 'Setting Title',
    setting_access: 'Setting Access',
    master_type: 'Master Name',
    master_details: 'Master Code',
    master_image_path: 'Master Image Path',
    message_type_id: 'Message Type',
    sender_id: 'Sender ID',
    enitity_id: 'Entity ID',
    api_key: 'API Key',
    template_id: 'Template ID',
    user_id: 'User ID',
    mobile_no: 'Mobile No.',
    owner_num: 'Owner Number',
    adminstrative_email: 'Admin Email',
    shorts_url: 'Short URL',
    mobileno_testing: 'Testing Mobile No',
    table_name: 'Table Name',
    seating_capacity: 'Seating Capacity',
    section_id: 'Section',
    supplier_id: 'Supplier',
    data_transfer: 'Data Transfer'
};

const colHeader = (key) => HEADER_MAP[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const CELL_EDITABLE_KEYS = [];

const FIELD_ICONS = {
    client_name: 'pi pi-building',
    no_of_branches: 'pi pi-sitemap',
    branch_prefix: 'pi pi-tag',
    dayend_mailpdf: 'pi pi-sun',
    short_url: 'pi pi-link',
    base_url: 'pi pi-server',
    branch_name: 'pi pi-map-marker',
    mobile: 'pi pi-phone',
    branch_title: 'pi pi-bookmark',
    branch_slno: 'pi pi-hashtag',
    invoice_prefix: 'pi pi-tag',
    org_name: 'pi pi-building',
    land_no: 'pi pi-phone',
    email_id: 'pi pi-envelope',
    gstin_no: 'pi pi-id-card',
    address1: 'pi pi-home',
    address2: 'pi pi-home',
    website_name: 'pi pi-globe',
    cin_no: 'pi pi-file',
    pincode: 'pi pi-map-marker',
    location: 'pi pi-map',
    social_order_percentage: 'pi pi-percentage',
    data_transfer: 'pi pi-sync',
    setting_name: 'pi pi-cog',
    setting_type_master: 'pi pi-sliders-h',
    setting_type: 'pi pi-pencil',
    setting_title: 'pi pi-file-edit',
    setting_access: 'pi pi-lock-open',
    master_type: 'pi pi-database',
    master_details: 'pi pi-code',
    master_image_path: 'pi pi-image',
    username: 'pi pi-user',
    password: 'pi pi-lock',
    sender_id: 'pi pi-id-card',
    enitity_id: 'pi pi-hashtag',
    entity_id: 'pi pi-hashtag',
    api_key: 'pi pi-key',
    template_id: 'pi pi-file',
    user_id: 'pi pi-user-edit',
    mobile_no: 'pi pi-phone',
    mobile_number: 'pi pi-phone',
    owner_num: 'pi pi-mobile',
    owner_number: 'pi pi-mobile',
    adminstrative_email: 'pi pi-at',
    administrative_email: 'pi pi-at',
    shorts_url: 'pi pi-link',
    short_url: 'pi pi-link',
    table_name: 'pi pi-table',
    seating_capacity: 'pi pi-users',
    effective_from: 'pi pi-calendar',
    status: 'pi pi-toggle-on',
    user_status: 'pi pi-toggle-on',
    branch_status: 'pi pi-toggle-on',
    setting_status: 'pi pi-toggle-on',
    table_status: 'pi pi-toggle-on',
    client_id: 'pi pi-building',
    branch_id: 'pi pi-map-marker',
    roleId: 'pi pi-shield',
    re_password: 'pi pi-lock',
    platform_id: 'pi pi-desktop',
    section_id: 'pi pi-th-large',
    supplier_id: 'pi pi-box',
    message_type_id: 'pi pi-comment',
    master_id: 'pi pi-database'
};

const FULL_WIDTH_FIELDS = new Set(['base_url', 'short_url', 'shorts_url', 'api_key', 'address1', 'address2']);

const Submasters = () => {
    const [active, setActive] = useState('client_creation');
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const [clients, setClients] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [roles, setRoles] = useState([]);
    const [sections, setSections] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [messageTypes, setMessageTypes] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [settingMasters, setSettingMasters] = useState([]);
    const [masterList, setMasterList] = useState([]);

    const [filterClient, setFilterClient] = useState(null);
    const [filterBranch, setFilterBranch] = useState(null);
    const [filterMessageType, setFilterMessageType] = useState(null);
    const [filterBranches, setFilterBranches] = useState([]);

    const [visible, setVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [formBranches, setFormBranches] = useState([]);

    // Bulk selection / status
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
    const [bulkStatusValue, setBulkStatusValue] = useState(null);
    const [bulkStatusEff, setBulkStatusEff] = useState(new Date());
    const [bulkAccessDialog, setBulkAccessDialog] = useState(false);
    const [bulkAccessValue, setBulkAccessValue] = useState(null);
    const [bulkAccessEff, setBulkAccessEff] = useState(new Date());

    // App-settings inline cell editing
    const [editedRows, setEditedRows] = useState([]);

    // SMS test
    const [smsTestDialog, setSmsTestDialog] = useState(false);
    const [smsTestForm, setSmsTestForm] = useState({
        auth_id: null,
        mobile: '',
        registered_mobile: '',
        mode: 'test',
        api_key: '',
        entity_id: '',
        template_id: '',
        user_id: '',
        sender_id: '',
        message_type_id: null,
        client_id: null,
        branch_id: null,
        branch_title: '',
        location: '',
        message_text: ''
    });
    const [smsTestLoading, setSmsTestLoading] = useState(false);
    const [smsTestResult, setSmsTestResult] = useState(null);

    const toast = useRef(null);
    const provisionAttemptedRef = useRef(null);
    const cfg = TABLE_CONFIGS[active];
    const tables = Object.entries(TABLE_CONFIGS)
        .filter(([value]) => value !== 'setting')
        .map(([value, c]) => ({ value, ...c }));

    const statusField = STATUS_FIELD_MAP[active];
    const baseFilteredData = (() => {
        let result = [...data];
        if (cfg.hasFilters) {
            if (filterClient && cfg.filterFields.includes('client')) result = result.filter((r) => String(r.client_id) === filterClient);
            if (filterBranch && cfg.filterFields.includes('branch')) result = result.filter((r) => String(r.branch_id) === filterBranch);
            if (filterMessageType && cfg.filterFields.includes('message_type')) result = result.filter((r) => String(r.message_type_id) === filterMessageType);
        }
        return result;
    })();
    const activeCount = statusField ? baseFilteredData.filter((r) => r[statusField] === 'Active').length : 0;
    const inactiveCount = statusField ? baseFilteredData.filter((r) => r[statusField] === 'Inactive').length : 0;

    useEffect(() => {
        const toArray = (res) => {
            if (Array.isArray(res)) return res;
            if (res && Array.isArray(res.data)) return res.data;
            if (res && Array.isArray(res.smsList)) return res.smsList;
            return [];
        };
        const ok = (result) => (result.status === 'fulfilled' ? toArray(result.value) : []);

        const loadLookups = async () => {
            const [clientRes, branchRes, roleRes, sectionRes, supplierRes, msgTypeRes, platformRes, settingRes, masterRes] = await Promise.allSettled([
                getServerMastersList('client_creation'),
                getServerMastersList('branch_creation'),
                getServerMastersList('roles'),
                getServerMastersList('sections'),
                getServerMastersList('supplier'),
                getServerMastersList('message_type'),
                getServerMastersList('platform'),
                getServerMastersList('setting'),
                getServerMastersList('master')
            ]);
            const clientRows = ok(clientRes);
            const branchRows = ok(branchRes);
            const roleRows = ok(roleRes);
            const sectionRows = ok(sectionRes);
            const supplierRows = ok(supplierRes);
            const messageTypeRows = ok(msgTypeRes);
            const platformRows = ok(platformRes);
            const settingRows = ok(settingRes);
            const masterRows = ok(masterRes);

            if (clientRows.length) setClients(clientRows.map((c) => ({ label: c.client_name, value: String(c.client_id) })));
            if (branchRows.length) setAllBranches(branchRows.map((b) => ({ label: b.branch_name, value: String(b.branch_id), clientId: String(b.client_id) })));
            if (roleRows.length) setRoles(roleRows.map((r) => ({ label: r.role, value: String(r.roleId) })));
            if (sectionRows.length) setSections(sectionRows.map((s) => ({ label: s.section_name, value: String(s.sectionId) })));
            if (supplierRows.length) setSuppliers(supplierRows.map((s) => ({ label: s.supplier_name, value: String(s.supplier_id) })));
            if (messageTypeRows.length) {
                const mapped = messageTypeRows.filter((m) => m.message_type && m.message_type_id != null).map((m) => ({ label: m.message_type, value: String(m.message_type_id) }));
                setMessageTypes(mapped);
            }
            if (platformRows.length) setPlatforms(platformRows.map((p) => ({ label: p.platform_name, value: String(p.platform_id) })));
            if (settingRows.length) setSettingMasters(settingRows);
            if (masterRows.length) setMasterList(masterRows.map((m) => ({ label: `${m.master_type} — ${m.master_details}`, value: String(m.master_id) })));
        };
        loadLookups();
    }, []);

    useEffect(() => {
        setData([]);
        setFilteredData([]);
        setGlobalFilterValue('');
        setStatusFilter(null);
        setFilterClient(null);
        setFilterBranch(null);
        setFilterMessageType(null);
        setFilterBranches([]);
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
        setSelectedRows([]);
        setEditedRows([]);
        loadData(active);
    }, [active]);

    useEffect(() => {
        applyFilters();
    }, [data, statusFilter, filterClient, filterBranch, filterMessageType]);

    useEffect(() => {
        if (active === 'application_setting') {
            setEditedRows(filteredData.map((r) => ({ ...r })));
        }
    }, [filteredData, active]);

    // Once both Client + Branch are picked on Application Settings, make sure every
    // active `setting` master entry has a row for this branch — a branch that has
    // never been configured would otherwise just show an empty table. Idempotent:
    // the backend only fills in setting_name gaps, so this is safe to fire on every
    // reload, and provisionAttemptedRef stops it from re-hitting the same combo
    // repeatedly when the catalog itself has nothing left to provision.
    useEffect(() => {
        if (active !== 'application_setting' || !filterClient || !filterBranch) return;
        const key = `${filterClient}:${filterBranch}`;
        const hasAny = data.some((r) => String(r.client_id) === String(filterClient) && String(r.branch_id) === String(filterBranch));
        if (hasAny || provisionAttemptedRef.current === key) return;
        provisionAttemptedRef.current = key;
        (async () => {
            try {
                const result = await provisionApplicationSettings({ client_id: filterClient, branch_id: filterBranch });
                if (result?.inserted) {
                    toast.current?.show({ severity: 'success', summary: 'Settings Provisioned', detail: result.message });
                    loadData(active);
                }
            } catch {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not auto-provision application settings for this branch' });
            }
        })();
    }, [active, filterClient, filterBranch, data]);

    useEffect(() => {
        if (filterClient) {
            const branches = allBranches.filter((b) => b.clientId === filterClient);
            setFilterBranches(branches);
            if (filterBranch && !branches.some((b) => b.value === filterBranch)) setFilterBranch(null);
        } else {
            setFilterBranches([]);
            setFilterBranch(null);
        }
    }, [filterClient, allBranches]);

    const parseListResponse = (json) => {
        if (Array.isArray(json)) return json;
        if (json && Array.isArray(json.data)) return json.data;
        if (json && Array.isArray(json.smsList)) return json.smsList;
        return [];
    };

    const loadData = async (table) => {
        setLoading(true);
        try {
            const json = await getServerMastersList(table);
            setData(parseListResponse(json));
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: `Could not load ${table}` });
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let result = [...data];
        if (statusFilter && statusField) result = result.filter((item) => item[statusField] === statusFilter);
        if (cfg.hasFilters) {
            if (filterClient && cfg.filterFields.includes('client')) result = result.filter((item) => String(item.client_id) === filterClient);
            if (filterBranch && cfg.filterFields.includes('branch')) result = result.filter((item) => String(item.branch_id) === filterBranch);
            if (filterMessageType && cfg.filterFields.includes('message_type')) result = result.filter((item) => String(item.message_type_id) === filterMessageType);
        }
        setFilteredData(result);
    };

    const branchesByClient = (clientId) => allBranches.filter((b) => b.clientId === String(clientId));
    const fmtDate = (d) => (moment(d).isValid() ? moment(d).format('DD-MM-YYYY') : d || '');
    const getClientName = (id) => clients.find((c) => String(c.value) === String(id))?.label || id;
    const getBranchName = (id) => allBranches.find((b) => String(b.value) === String(id))?.label || id;
    const getRoleName = (id) => roles.find((r) => String(r.value) === String(id))?.label || id;
    const getSectionName = (id) => sections.find((s) => String(s.value) === String(id))?.label || id;
    const getSupplierName = (id) => suppliers.find((s) => String(s.value) === String(id))?.label || id;
    const getMessageTypeName = (id) => messageTypes.find((m) => String(m.value) === String(id))?.label || id;
    const getPlatformName = (id) => platforms.find((p) => String(p.value) === String(id))?.label || id;

    const validateForm = (fields, fd) => {
        const validationRules = FIELD_VALIDATION_RULES[active] || {};
        const newErrors = {};
        fields.forEach((f) => {
            const rule = validationRules[f.key];
            if (!rule) return;
            const value = fd[f.key];
            // 0 is a legitimate value for numeric/select fields (e.g. Setting Access) —
            // only null/undefined/empty-string count as "not filled in".
            const isEmpty = value === null || value === undefined || (typeof value === 'string' && !value.trim());
            // Confirm-password cross-field validation
            if (rule.type === 'confirm_password') {
                if (fd.password && isEmpty) {
                    newErrors[f.key] = 'Please confirm your password';
                } else if (fd.password && value !== fd.password) {
                    newErrors[f.key] = 'Passwords do not match';
                }
                return;
            }
            if (rule.required && isEmpty) {
                newErrors[f.key] = `${rule.label} is required`;
                return;
            }
            if (isEmpty) return;
            if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) newErrors[f.key] = `${rule.label} must not exceed ${rule.maxLength} characters`;
            if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) newErrors[f.key] = `${rule.label} must be at least ${rule.minLength} characters`;
            if (rule.type === 'number' && isNaN(parseFloat(value))) newErrors[f.key] = `${rule.label} must be a valid number`;
            if (rule.type === 'url' && value) {
                try {
                    new URL(value);
                } catch {
                    newErrors[f.key] = `${rule.label} must be a valid URL`;
                }
            }
            if (rule.type === 'mobile' && value && !/^[0-9]{10,15}$/.test(value.replace(/[\s\-\+]/g, ''))) newErrors[f.key] = `${rule.label} must be a valid phone number`;
            if (rule.type === 'email' && value && !/\S+@\S+\.\S+/.test(value)) newErrors[f.key] = `${rule.label} must be a valid email address`;
        });
        return newErrors;
    };

    const checkDuplicates = (fd) => {
        const uniqueCombinations = UNIQUE_FIELD_COMBINATIONS[active] || [];
        for (const fieldCombination of uniqueCombinations) {
            const values = fieldCombination.map((field) => fd[field]);
            if (values.some((v) => !v || (typeof v === 'string' && !v.trim()))) continue;
            const isDuplicate = data.some((row) => {
                if (isEditing && fd[cfg.pk] && String(row[cfg.pk]) === String(fd[cfg.pk])) return false;
                return fieldCombination.every(
                    (field, index) =>
                        String(row[field] || '')
                            .toLowerCase()
                            .trim() ===
                        String(values[index] || '')
                            .toLowerCase()
                            .trim()
                );
            });
            if (isDuplicate) {
                const fieldLabels = fieldCombination.map((f) => FIELD_VALIDATION_RULES[active]?.[f]?.label || f).join(' + ');
                toast.current?.show({ severity: 'warn', summary: 'Duplicate Record', detail: `A record with this ${fieldLabels} combination already exists`, life: 5000 });
                return false;
            }
        }
        return true;
    };

    const saveItem = async () => {
        const payload = { ...formData };
        if (!isEditing) delete payload[cfg.pk];
        delete payload.re_password;
        // setting_type_master is a UI-only lookup for narrowing Setting Name options —
        // it has no column in application_setting and must never reach the save API.
        delete payload.setting_type_master;
        if (payload.effective_from instanceof Date) payload.effective_from = moment(payload.effective_from).format('YYYY-MM-DD');
        try {
            const json = await saveServerMaster(active, payload);
            if (json.status === 'success') {
                toast.current?.show({ severity: 'success', summary: isEditing ? 'Updated' : 'Saved', detail: isEditing ? 'Record updated successfully' : 'Record created successfully' });
                setVisible(false);
                setFormData({});
                setErrors({});
                loadData(active);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: json.message || 'Operation failed' });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Network Error', detail: 'Could not reach the server' });
        }
    };

    // Bulk status change for application_setting and masters_creation
    const handleBulkStatusChange = async () => {
        if (!selectedRows.length || !bulkStatusValue || !bulkStatusEff) return;
        try {
            const effective_from = moment(bulkStatusEff).format('YYYY-MM-DD');
            await Promise.all(
                selectedRows.map((row) => {
                    const payload = { ...row, [statusField]: bulkStatusValue, effective_from, data_transfer: 'pending' };
                    return saveServerMaster(active, payload);
                })
            );
            toast.current?.show({ severity: 'success', summary: 'Updated', detail: `${selectedRows.length} record(s) set to ${bulkStatusValue}` });
            setBulkStatusDialog(false);
            setSelectedRows([]);
            setBulkStatusValue(null);
            setBulkStatusEff(new Date());
            loadData(active);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Network Error', detail: 'Could not reach the server' });
        }
    };

    // Bulk Setting Access change for application_setting
    const handleBulkAccessChange = async () => {
        if (!selectedRows.length || bulkAccessValue === null || bulkAccessValue === undefined || !bulkAccessEff) return;
        try {
            const effective_from = moment(bulkAccessEff).format('YYYY-MM-DD');
            await Promise.all(
                selectedRows.map((row) => {
                    const payload = { ...row, setting_access: bulkAccessValue, effective_from, data_transfer: 'pending' };
                    return saveServerMaster(active, payload);
                })
            );
            toast.current?.show({ severity: 'success', summary: 'Updated', detail: `${selectedRows.length} record(s) set to access ${bulkAccessValue}` });
            setBulkAccessDialog(false);
            setSelectedRows([]);
            setBulkAccessValue(null);
            setBulkAccessEff(new Date());
            loadData(active);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Network Error', detail: 'Could not reach the server' });
        }
    };

    // Inline cell editing save for application_setting
    const handleSaveAllSettings = async () => {
        const changes = editedRows.filter((row, idx) => JSON.stringify(row) !== JSON.stringify(filteredData[idx]));
        if (!changes.length) {
            toast.current?.show({ severity: 'info', summary: 'No Changes', detail: 'Nothing to update' });
            return;
        }
        try {
            await Promise.all(
                changes.map((row) =>
                    saveServerMaster('application_setting', {
                        id: row.app_setting_id,
                        settingName: row.setting_name,
                        settingAccess: row.setting_access,
                        settingType: row.setting_type,
                        settingTitle: row.setting_title,
                        settingStatus: row.setting_status,
                        effectiveFrom: row.effective_from instanceof Date ? moment(row.effective_from).format('YYYY-MM-DD') : row.effective_from
                    })
                )
            );
            toast.current?.show({ severity: 'success', summary: 'Saved', detail: `${changes.length} setting(s) updated` });
            loadData(active);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save changes' });
        }
    };

    const onCellEditComplete = (e) => {
        const { rowIndex, field, newValue } = e;
        const updated = [...editedRows];
        updated[rowIndex] = { ...updated[rowIndex], [field]: newValue };
        setEditedRows(updated);
    };

    const cellEditor = (key, options) => {
        if (key === 'setting_status') return <Dropdown value={options.value} options={STATUS_OPTIONS} onChange={(e) => options.editorCallback(e.value)} placeholder="Select status" className="w-full" />;
        if (key === 'setting_access') return <Dropdown value={options.value !== undefined ? options.value : ''} options={ACCESS_OPTIONS} onChange={(e) => options.editorCallback(e.value)} placeholder="Access" className="w-full" />;
        return <InputText value={options.value || ''} onChange={(e) => options.editorCallback(e.target.value)} className="w-full" />;
    };

    // SMS test
    const openSmsTest = (row) => {
        setSmsTestForm({
            auth_id: row.auth_id,
            mobile: '',
            registered_mobile: row.mobile_no || '',
            mode: 'test',
            api_key: row.api_key || '',
            entity_id: row.enitity_id || '',
            template_id: row.template_id || '',
            user_id: row.user_id || '',
            sender_id: row.sender_id || '',
            message_type_id: row.message_type_id || null,
            client_id: row.client_id || null,
            branch_id: row.branch_id || null,
            branch_title: row.branch_title || '',
            location: row.location || '',
            message_text: 'This is a test SMS from the GSPOS system. Config verified successfully.'
        });
        setSmsTestResult(null);
        setSmsTestDialog(true);
    };

    const handleSmsTest = async () => {
        const isLive = smsTestForm.mode === 'live';
        const targetMobile = isLive ? smsTestForm.registered_mobile : smsTestForm.mobile;
        if (!targetMobile || !String(targetMobile).trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Required', detail: isLive ? 'No registered mobile found on this config' : 'Enter a mobile number to test' });
            return;
        }
        if (!smsTestForm.message_text || !smsTestForm.message_text.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Enter a test message' });
            return;
        }
        setSmsTestLoading(true);
        setSmsTestResult(null);
        try {
            const payload = {
                auth_id: smsTestForm.auth_id,
                mode: smsTestForm.mode,
                mobile: targetMobile,
                api_key: smsTestForm.api_key,
                entity_id: smsTestForm.entity_id,
                template_id: smsTestForm.template_id,
                user_id: smsTestForm.user_id,
                sender_id: smsTestForm.sender_id,
                message_text: smsTestForm.message_text,
                message_type_id: smsTestForm.message_type_id,
                client_id: smsTestForm.client_id,
                branch_id: smsTestForm.branch_id,
                branch_title: smsTestForm.branch_title,
                location: smsTestForm.location
            };
            const json = await sendSmsTest(payload);
            const ok = json.status === 'success';
            setSmsTestResult({ ok, message: json.message || (ok ? 'SMS sent successfully' : 'Failed to send SMS'), gateway: json.gateway_response || null });
            if (ok) toast.current?.show({ severity: 'success', summary: 'SMS Sent', detail: `${isLive ? 'Live' : 'Test'} SMS dispatched to ${targetMobile}` });
            else toast.current?.show({ severity: 'error', summary: 'Failed', detail: json.message || 'Gateway rejected the request' });
        } catch {
            setSmsTestResult({ ok: false, message: 'Could not reach the server. Check your network.' });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not reach the server' });
        } finally {
            setSmsTestLoading(false);
        }
    };

    const handleResetFilters = () => {
        setGlobalFilterValue('');
        setStatusFilter(null);
        setFilterClient(null);
        setFilterBranch(null);
        setFilterMessageType(null);
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    };

    const getCellText = (key, row) => {
        const val = row[key];
        if (['status', 'user_status', 'branch_status', 'master_status', 'setting_status', 'table_status'].includes(key)) return val || '';
        if (key === 'client_id') return getClientName(val);
        if (key === 'branch_id') return getBranchName(val);
        if (key === 'roleId') return getRoleName(val);
        if (key === 'section_id') return getSectionName(val);
        if (key === 'supplier_id') return getSupplierName(val);
        if (key === 'message_type_id') return getMessageTypeName(val);
        if (key === 'platform_id') return getPlatformName(val);
        if (key === 'effective_from' || key === 'config_date') return fmtDate(val);
        if (key === 'data_transfer') return val || '';
        return String(val ?? '');
    };

    const exportToExcel = () => {
        const exportData = filteredData.map((row, i) => {
            const obj = { '#': i + 1 };
            cfg.visibleCols.forEach((key) => {
                obj[colHeader(key)] = getCellText(key, row);
            });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, cfg.label.substring(0, 31));
        XLSX.writeFile(wb, `${cfg.label.replace(/\s+/g, '_')}_${moment().format('DDMMYYYY')}.xlsx`);
    };

    const openAdd = () => {
        setIsEditing(false);
        const initial = { effective_from: new Date() };
        let branches = [];
        // If the list is already scoped to a Client (and Branch) via the filter bar,
        // carry that same scope into the Add form instead of starting blank.
        if (cfg.hasFilters && cfg.filterFields.includes('client') && filterClient) {
            initial.client_id = filterClient;
            branches = branchesByClient(filterClient);
            if (cfg.filterFields.includes('branch') && filterBranch && branches.some((b) => String(b.value) === String(filterBranch))) {
                initial.branch_id = filterBranch;
            }
        }
        setFormData(initial);
        setFormBranches(branches);
        setErrors({});
        setVisible(true);
    };

    const openEdit = (row) => {
        setIsEditing(true);
        const fd = { ...row };
        if (fd.effective_from) fd.effective_from = moment(fd.effective_from, ['YYYY-MM-DD', 'DD-MM-YYYY']).toDate();
        ['client_id', 'branch_id', 'roleId', 'section_id', 'supplier_id', 'message_type_id', 'platform_id', 'master_id'].forEach((k) => {
            if (fd[k] !== undefined) fd[k] = String(fd[k]);
        });
        // ACCESS_OPTIONS uses numeric values (0/1/2/3) — the API can return this as a
        // numeric string, which would fail the Dropdown's strict-equality match and
        // show no selection even though the row has a value.
        if (fd.setting_access !== undefined && fd.setting_access !== null) fd.setting_access = Number(fd.setting_access);
        // Map SMS API field name typos to form field names
        if (active === 'sms_authorize_final') {
            fd.entity_id = fd.enitity_id || fd.entity_id || '';
            fd.mobile_number = fd.mobile_no || fd.mobile_number || '';
            fd.owner_number = fd.owner_num || fd.owner_number || '';
            fd.administrative_email = fd.adminstrative_email || fd.administrative_email || '';
            fd.short_url = fd.shorts_url || fd.short_url || '';
        }
        // Setting Type (Master) is UI-only and never persisted — re-derive it from
        // the matching catalog row so the Setting Name dropdown stays usable on edit.
        if (active === 'application_setting' && fd.setting_name) {
            const match = settingMasters.find((s) => s.setting_name === fd.setting_name);
            if (match) fd.setting_type_master = match.setting_type;
        }
        setFormData(fd);
        setErrors({});
        if (fd.client_id) setFormBranches(branchesByClient(fd.client_id));
        setVisible(true);
    };

    const onFormClientChange = (clientId) => {
        setFormData((prev) => ({ ...prev, client_id: clientId, branch_id: '' }));
        setFormBranches(clientId ? branchesByClient(clientId) : []);
        if (errors.client_id)
            setErrors((p) => {
                const n = { ...p };
                delete n.client_id;
                return n;
            });
    };

    // Application Settings — Setting Name is sourced from the `setting` master
    // table (fetched into settingMasters above) and narrowed by the Setting Type
    // (Master) lookup dropdown, which only unlocks once a Branch is picked.
    // Setting Type itself is a separate free-text field that is what actually
    // gets written to application_setting.setting_type — the lookup dropdown is
    // UI-only (stripped from the payload before save) and just helps narrow the
    // Setting Name choices. Choosing a Name auto-fills Setting Title from the
    // matched master row.
    const activeSettingMasters = settingMasters.filter((s) => (s.status || 'Active') !== 'Inactive');
    const settingTypeOptions = [...new Set(activeSettingMasters.map((s) => s.setting_type).filter(Boolean))].map((t) => ({ label: t, value: t }));
    const settingNameOptions = activeSettingMasters
        .filter((s) => !formData.setting_type_master || s.setting_type === formData.setting_type_master)
        .map((s) => ({ label: s.setting_name, value: s.setting_name }));

    const onFormSettingTypeMasterChange = (type) => {
        setFormData((prev) => ({ ...prev, setting_type_master: type, setting_name: '', setting_title: '' }));
        if (errors.setting_type_master)
            setErrors((p) => {
                const n = { ...p };
                delete n.setting_type_master;
                return n;
            });
    };

    const onFormSettingNameChange = (name) => {
        const match = settingMasters.find((s) => s.setting_name === name && s.setting_type === formData.setting_type_master);
        setFormData((prev) => ({ ...prev, setting_name: name, setting_title: match?.setting_title || prev.setting_title || '' }));
        if (errors.setting_name)
            setErrors((p) => {
                const n = { ...p };
                delete n.setting_name;
                return n;
            });
    };

    const handleFieldChange = (key, value) => {
        setFormData((p) => ({ ...p, [key]: value }));
        if (errors[key])
            setErrors((p) => {
                const n = { ...p };
                delete n[key];
                return n;
            });
    };

    const handleFieldBlur = (key) => {
        const rule = (FIELD_VALIDATION_RULES[active] || {})[key];
        if (!rule?.required) return;
        const value = formData[key];
        if (value === undefined || value === null || (typeof value === 'string' && !value.trim())) {
            setErrors((p) => ({ ...p, [key]: `${rule.label} is required` }));
        }
    };

    const renderCell = (key, row) => {
        const val = row[key];
        if (['status', 'user_status', 'branch_status', 'master_status', 'setting_status', 'table_status'].includes(key))
            return <Tag value={val} severity={val === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '68px', textAlign: 'center', fontSize: '0.73rem', fontWeight: 600, letterSpacing: '0.03em', padding: '0.22rem 0.65rem' }} />;
        if (key === 'client_id') return getClientName(val);
        if (key === 'branch_id') return getBranchName(val);
        if (key === 'roleId') return getRoleName(val);
        if (key === 'section_id') return getSectionName(val);
        if (key === 'supplier_id') return getSupplierName(val);
        if (key === 'message_type_id') return getMessageTypeName(val);
        if (key === 'platform_id') return getPlatformName(val);
        if (key === 'effective_from' || key === 'config_date') return fmtDate(val);
        if (key === 'data_transfer') {
            if (!val) return '—';
            const isUpdated = String(val).toLowerCase() === 'updated';
            return <Tag value={val} severity={isUpdated ? 'success' : 'warning'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;
        }
        return val ?? '—';
    };

    // Combined cell layouts matching the Product List datatable — Client (top) +
    // Branch (bottom), and Effective Date (top) + Sync Status (bottom) — so both
    // stay compact instead of taking two separate columns.
    const clientBranchBody = (row) => (
        <div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35 }}>{getClientName(row.client_id)}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.72rem', color: '#6d28d9', marginTop: '0.15rem', fontWeight: '600' }}>{getBranchName(row.branch_id)}</div>
        </div>
    );
    // Branch list identifies the row by branch_name directly (no separate branch_id
    // column exists there) — same layout, just reading the name off the row itself.
    const clientBranchNameBody = (row) => (
        <div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35 }}>{getClientName(row.client_id)}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.72rem', color: '#6d28d9', marginTop: '0.15rem', fontWeight: '600' }}>{row.branch_name ?? '—'}</div>
        </div>
    );
    const dateTransferBody = (row) => {
        const v = (row.data_transfer || '').toLowerCase();
        const colorMap = { pending: '#d97706', added: '#16a34a', updated: '#2563eb', deleted: '#dc2626' };
        const syncColor = colorMap[v] || '#64748b';
        return (
            <div>
                <div style={{ fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35, whiteSpace: 'nowrap' }}>{fmtDate(row.effective_from) || '—'}</div>
                <div style={{ fontSize: '0.7rem', color: syncColor, marginTop: '0.15rem', fontWeight: '700', textTransform: 'capitalize' }}>{row.data_transfer || 'N/A'}</div>
            </div>
        );
    };

    // Collapses known adjacent column pairs (Client+Branch, Effective Date+Sync
    // Status) into a single combined column, same as the Product List datatable,
    // instead of rendering every visibleCols entry as its own separate column.
    const COMBINED_COLUMNS = [
        { keys: ['client_id', 'branch_id'], header: 'Client / Branch', body: clientBranchBody },
        { keys: ['client_id', 'branch_name'], header: 'Client / Branch', body: clientBranchNameBody },
        { keys: ['effective_from', 'data_transfer'], header: 'Date / Sync', body: dateTransferBody }
    ];
    const getRenderColumns = (visibleCols) => {
        const cols = [];
        for (let i = 0; i < visibleCols.length; ) {
            const combo = COMBINED_COLUMNS.find((c) => c.keys[0] === visibleCols[i] && visibleCols[i + 1] === c.keys[1]);
            if (combo) {
                cols.push({ key: combo.keys[0], header: combo.header, body: combo.body });
                i += combo.keys.length;
            } else {
                const key = visibleCols[i];
                cols.push({ key, header: colHeader(key), body: (rd) => renderCell(key, rd) });
                i += 1;
            }
        }
        return cols;
    };

    const confirmSave = () => {
        const errs = validateForm(cfg.formFields, formData);
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fix the highlighted fields' });
            return;
        }
        if (!checkDuplicates(formData)) return;
        confirmDialog({
            message: isEditing ? `Are you sure you want to update this ${cfg.label}?` : `Are you sure you want to add this new ${cfg.label}?`,
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Proceed',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => saveItem()
        });
    };

    const renderField = (f) => {
        const val = formData[f.key];
        const err = errors[f.key];
        const cls = err ? 'p-invalid' : '';
        const icon = FIELD_ICONS[f.key];
        const onBlur = () => handleFieldBlur(f.key);

        const textInput = (type = 'text') => {
            const inputEl = <InputText type={type} value={val || ''} onChange={(e) => handleFieldChange(f.key, e.target.value)} onBlur={onBlur} className={`w-full ${cls}`} placeholder={`Enter ${f.label.toLowerCase()}`} />;
            return icon ? (
                <div className="sub-icon-input">
                    <i className={`${icon} sub-input-icon`} />
                    {inputEl}
                </div>
            ) : (
                inputEl
            );
        };

        const input = () => {
            switch (f.type) {
                case 'status':
                    return <Dropdown value={val} options={STATUS_OPTIONS} onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Status" className={`w-full ${cls}`} />;
                case 'access':
                    return <Dropdown value={val !== undefined ? val : ''} options={ACCESS_OPTIONS} onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Access" className={`w-full ${cls}`} />;
                case 'client':
                    return <Dropdown value={val} options={clients} optionLabel="label" optionValue="value" onChange={(e) => onFormClientChange(e.value)} placeholder="Select Client" className={`w-full ${cls}`} filter />;
                case 'branch':
                    return (
                        <Dropdown
                            value={val}
                            options={formBranches}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => handleFieldChange(f.key, e.value)}
                            placeholder={formBranches.length ? 'Select Branch' : 'Select Client first'}
                            className={`w-full ${cls}`}
                            disabled={!formData.client_id}
                            filter
                            showClear
                        />
                    );
                case 'setting_type':
                    return (
                        <Dropdown
                            value={val}
                            options={settingTypeOptions}
                            onChange={(e) => onFormSettingTypeMasterChange(e.value)}
                            placeholder={formData.branch_id ? 'Select Setting Type' : 'Select Branch first'}
                            className={`w-full ${cls}`}
                            disabled={!formData.branch_id}
                            filter
                        />
                    );
                case 'setting_name':
                    return (
                        <Dropdown
                            value={val}
                            options={settingNameOptions}
                            onChange={(e) => onFormSettingNameChange(e.value)}
                            placeholder={formData.setting_type_master ? 'Select or type Setting Name' : 'Select Setting Type first'}
                            className={`w-full ${cls}`}
                            disabled={!formData.setting_type_master}
                            editable
                            filter
                        />
                    );
                case 'setting_type_custom':
                    return (
                        <Dropdown
                            value={val}
                            options={settingTypeOptions}
                            onChange={(e) => handleFieldChange(f.key, e.value)}
                            placeholder="Select or type Setting Type"
                            className={`w-full ${cls}`}
                            editable
                            filter
                        />
                    );
                case 'role':
                    return <Dropdown value={val} options={roles} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Role" className={`w-full ${cls}`} />;
                case 'section':
                    return <Dropdown value={val} options={sections} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Section" className={`w-full ${cls}`} filter />;
                case 'supplier':
                    return <Dropdown value={val} options={suppliers} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Supplier" className={`w-full ${cls}`} filter />;
                case 'message_type':
                    return <Dropdown value={val} options={messageTypes} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} placeholder="Select Message Type" className={`w-full ${cls}`} filter />;
                case 'date':
                    return <Calendar value={val instanceof Date ? val : null} onChange={(e) => handleFieldChange(f.key, e.value)} dateFormat="dd-mm-yy" placeholder="DD-MM-YYYY" className={`w-full ${cls}`} showIcon />;
                case 'email':
                    return textInput('email');
                case 'url':
                    return textInput('url');
                case 'password':
                    return textInput('password');
                case 'confirm_password':
                    return textInput('password');
                case 'platform':
                    return <Dropdown value={val} options={platforms} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} onBlur={onBlur} placeholder="Select Platform" className={`w-full ${cls}`} filter />;
                case 'master':
                    return <Dropdown value={val} options={masterList} optionLabel="label" optionValue="value" onChange={(e) => handleFieldChange(f.key, e.value)} onBlur={onBlur} placeholder="Select Master" className={`w-full ${cls}`} filter />;
                case 'data_transfer':
                    return <Dropdown value={val} options={DATA_TRANSFER_OPTIONS} onChange={(e) => handleFieldChange(f.key, e.value)} onBlur={onBlur} placeholder="Select Data Transfer" className={`w-full ${cls}`} />;
                default:
                    return textInput();
            }
        };

        return (
            <div key={f.key} className="sub-field-group">
                <label className="sub-field-label">
                    {icon && <i className={`${icon} sub-field-label-icon`} />}
                    {f.label}
                    {f.required && <span className="required-mark"> *</span>}
                </label>
                {input()}
                {err && (
                    <small className="sub-error-msg">
                        <i className="pi pi-exclamation-circle" /> {err}
                    </small>
                )}
            </div>
        );
    };

    const actionBodyTemplate = (rd) => (
        <div className="flex gap-1 justify-content-center">
            <Button icon="pi pi-pencil" rounded text className="p-button-warning" onClick={() => openEdit(rd)} tooltip="Edit" tooltipOptions={{ position: 'top' }} />
            {cfg.hasSmsTest && <Button icon="pi pi-send" rounded text className="p-button-info" onClick={() => openSmsTest(rd)} tooltip="Test SMS" tooltipOptions={{ position: 'top' }} />}
        </div>
    );

    const hasAppSettingChanges = active === 'application_setting' && editedRows.some((row, idx) => JSON.stringify(row) !== JSON.stringify(filteredData[idx]));

    const isSmsModule = active === 'sms_authorize_final';

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="masters-page-header">
                <h2>Sub Masters</h2>
                <p>View and manage sub master records</p>
            </div>

            <div className="list-filter-bar">
                <div className="filter-field" style={{ flex: '1 1 200px', minWidth: '175px' }}>
                    <label htmlFor="module">Module</label>
                    <Dropdown id="module" value={active} options={tables} onChange={(e) => setActive(e.value)} optionLabel="label" optionValue="value" placeholder="Select Module" className="w-full" />
                </div>
                {cfg.hasFilters && cfg.filterFields.includes('client') && (
                    <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                        <label>Client</label>
                        <Dropdown value={filterClient} options={clients} optionLabel="label" optionValue="value" onChange={(e) => setFilterClient(e.value)} placeholder="Filter by Client" className="w-full" filter showClear />
                    </div>
                )}
                {cfg.hasFilters && cfg.filterFields.includes('branch') && (
                    <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                        <label>Branch</label>
                        <Dropdown
                            value={filterBranch}
                            options={filterBranches}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setFilterBranch(e.value)}
                            placeholder={filterClient ? 'Filter by Branch' : 'Select Client First'}
                            className="w-full"
                            filter
                            showClear
                            disabled={!filterClient}
                        />
                    </div>
                )}
                {cfg.hasFilters && cfg.filterFields.includes('message_type') && (
                    <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                        <label>Message Type</label>
                        <Dropdown value={filterMessageType} options={messageTypes} optionLabel="label" optionValue="value" onChange={(e) => setFilterMessageType(e.value)} placeholder="Filter by Type" className="w-full" filter showClear />
                    </div>
                )}
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button icon="pi pi-refresh" className="p-button-secondary" tooltip="Reset Filters" tooltipOptions={{ position: 'top' }} onClick={handleResetFilters} />
                </div>
            </div>

            <Card className="bill-reports-container">
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <>
                        <div className="sub-masters-table-wrap">
                            <DataTable
                                value={cfg.hasCellEdit ? editedRows : filteredData}
                                filters={filters}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                                showGridlines
                                stripedRows
                                scrollable
                                scrollHeight="flex"
                                header={
                                    <div className="flex flex-wrap justify-content-between align-items-center gap-2">
                                        <div className="flex align-items-center gap-2 flex-wrap">
                                            <h3 className="text-lg font-semibold m-0">{cfg.label} List</h3>
                                            <div className="category-status-tabs">
                                                <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Active' ? null : 'Active')}>
                                                    <i className="pi pi-check-circle" /> Active <span className="tab-count">{activeCount}</span>
                                                </button>
                                                <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Inactive' ? null : 'Inactive')}>
                                                    <i className="pi pi-times-circle" /> Inactive <span className="tab-count">{inactiveCount}</span>
                                                </button>
                                            </div>
                                            {cfg.hasRowSelection && selectedRows.length > 0 && (
                                                <Button
                                                    label={`Change Status (${selectedRows.length})`}
                                                    icon="pi pi-list"
                                                    className="p-button-warning p-button-sm"
                                                    onClick={() => {
                                                        setBulkStatusEff(new Date());
                                                        setBulkStatusDialog(true);
                                                    }}
                                                />
                                            )}
                                            {active === 'application_setting' && selectedRows.length > 0 && (
                                                <Button
                                                    label={`Change Access (${selectedRows.length})`}
                                                    icon="pi pi-lock-open"
                                                    className="p-button-help p-button-sm"
                                                    onClick={() => {
                                                        setBulkAccessEff(new Date());
                                                        setBulkAccessDialog(true);
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-2 align-items-center">
                                            <Button icon="pi pi-file-excel" className="masters-excel-btn" onClick={exportToExcel} tooltip="Export to Excel" tooltipOptions={{ position: 'top' }} />
                                            <span className="p-input-icon-left" style={{ minWidth: '200px' }}>
                                                <i className="pi pi-search" />
                                                <InputText
                                                    value={globalFilterValue}
                                                    onChange={(e) => {
                                                        setGlobalFilterValue(e.target.value);
                                                        setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                                                    }}
                                                    placeholder={`Search ${cfg.label}...`}
                                                    className="w-full p-inputtext-sm"
                                                />
                                            </span>
                                            {active !== 'application_setting' && <Button label="Add New" icon="pi pi-plus" className="p-button-primary" onClick={openAdd} />}
                                        </div>
                                    </div>
                                }
                                tableStyle={{ minWidth: '38rem', width: '100%' }}
                                className="sub-masters-table"
                                emptyMessage={`No ${statusFilter ? statusFilter.toLowerCase() + ' ' : ''}${cfg.label.toLowerCase()} records found`}
                                selection={cfg.hasRowSelection ? selectedRows : undefined}
                                onSelectionChange={cfg.hasRowSelection ? (e) => setSelectedRows(e.value) : undefined}
                                editMode={cfg.hasCellEdit ? 'cell' : undefined}
                            >
                                {cfg.hasRowSelection && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />}
                                {getRenderColumns(cfg.visibleCols).map((col) => (
                                    <Column
                                        key={`${active}_${col.key}`}
                                        field={col.key}
                                        header={col.header}
                                        sortable
                                        body={col.body}
                                        {...(cfg.hasCellEdit && CELL_EDITABLE_KEYS.includes(col.key) ? { editor: (opts) => cellEditor(col.key, opts), onCellEditComplete } : {})}
                                    />
                                ))}
                                {!cfg.hideActions && (
                                    <Column header="Actions" body={actionBodyTemplate} frozen alignFrozen="right" style={{ width: cfg.hasSmsTest ? '110px' : '90px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                                )}
                            </DataTable>
                        </div>

                        {hasAppSettingChanges && (
                            <div className="mt-3 flex justify-content-end">
                                <Button label="Save All Changes" icon="pi pi-check" className="p-button-success" onClick={handleSaveAllSettings} />
                            </div>
                        )}
                    </>
                )}
            </Card>

            {/* Bulk Status Change Dialog */}
            <Dialog
                visible={bulkStatusDialog}
                onHide={() => {
                    setBulkStatusDialog(false);
                    setBulkStatusValue(null);
                    setBulkStatusEff(new Date());
                }}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className="pi pi-list" />
                        </span>
                        <span className="sub-dialog-header-text">Change Status — {selectedRows.length} Record(s)</span>
                    </div>
                }
                style={{ width: '420px', maxWidth: '96vw' }}
                modal
                className="sub-master-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="sub-dialog-cancel"
                            onClick={() => {
                                setBulkStatusDialog(false);
                                setBulkStatusValue(null);
                                setBulkStatusEff(new Date());
                            }}
                        />
                        <Button label="Update" icon="pi pi-check" className="sub-dialog-save" onClick={handleBulkStatusChange} disabled={!bulkStatusValue || !bulkStatusEff} />
                    </div>
                }
            >
                <div className="sub-form-grid" style={{ gridTemplateColumns: '1fr', paddingBottom: '0.5rem' }}>
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-toggle-on sub-field-label-icon" /> New Status <span className="required-mark">*</span>
                        </label>
                        <Dropdown value={bulkStatusValue} options={STATUS_OPTIONS} onChange={(e) => setBulkStatusValue(e.value)} placeholder="Select Status" className="w-full" />
                    </div>
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-calendar sub-field-label-icon" /> Effective From <span className="required-mark">*</span>
                        </label>
                        <Calendar value={bulkStatusEff} onChange={(e) => setBulkStatusEff(e.value)} dateFormat="dd-mm-yy" placeholder="DD-MM-YYYY" className="w-full" showIcon />
                    </div>
                </div>
            </Dialog>

            {/* Bulk Setting Access Change Dialog */}
            <Dialog
                visible={bulkAccessDialog}
                onHide={() => {
                    setBulkAccessDialog(false);
                    setBulkAccessValue(null);
                    setBulkAccessEff(new Date());
                }}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className="pi pi-lock-open" />
                        </span>
                        <span className="sub-dialog-header-text">Change Setting Access — {selectedRows.length} Record(s)</span>
                    </div>
                }
                style={{ width: '420px', maxWidth: '96vw' }}
                modal
                className="sub-master-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="sub-dialog-cancel"
                            onClick={() => {
                                setBulkAccessDialog(false);
                                setBulkAccessValue(null);
                                setBulkAccessEff(new Date());
                            }}
                        />
                        <Button label="Update" icon="pi pi-check" className="sub-dialog-save" onClick={handleBulkAccessChange} disabled={bulkAccessValue === null || bulkAccessValue === undefined || !bulkAccessEff} />
                    </div>
                }
            >
                <div className="sub-form-grid" style={{ gridTemplateColumns: '1fr', paddingBottom: '0.5rem' }}>
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-lock-open sub-field-label-icon" /> New Setting Access <span className="required-mark">*</span>
                        </label>
                        <Dropdown value={bulkAccessValue} options={ACCESS_OPTIONS} onChange={(e) => setBulkAccessValue(e.value)} placeholder="Select Access" className="w-full" />
                    </div>
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-calendar sub-field-label-icon" /> Effective From <span className="required-mark">*</span>
                        </label>
                        <Calendar value={bulkAccessEff} onChange={(e) => setBulkAccessEff(e.value)} dateFormat="dd-mm-yy" placeholder="DD-MM-YYYY" className="w-full" showIcon />
                    </div>
                </div>
            </Dialog>

            {/* SMS Test Dialog */}
            <Dialog
                visible={smsTestDialog}
                onHide={() => {
                    setSmsTestDialog(false);
                    setSmsTestResult(null);
                }}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className="pi pi-send" />
                        </span>
                        <span className="sub-dialog-header-text">SMS Test Console</span>
                    </div>
                }
                style={{ width: '480px', maxWidth: '96vw' }}
                position="center"
                modal
                className="sub-master-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            className="sub-dialog-cancel"
                            onClick={() => {
                                setSmsTestDialog(false);
                                setSmsTestResult(null);
                            }}
                        />
                        <Button
                            label={smsTestForm.mode === 'live' ? 'Send Live SMS' : 'Send Test SMS'}
                            icon="pi pi-send"
                            className={`sub-dialog-save${smsTestForm.mode === 'live' ? ' sms-live-btn' : ''}`}
                            onClick={handleSmsTest}
                            loading={smsTestLoading}
                        />
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', paddingBottom: '0.5rem' }}>
                    {/* Mode toggle */}
                    <div className="sms-mode-toggle">
                        <button
                            className={`sms-mode-btn${smsTestForm.mode === 'test' ? ' active' : ''}`}
                            onClick={() => {
                                setSmsTestForm((p) => ({ ...p, mode: 'test' }));
                                setSmsTestResult(null);
                            }}
                        >
                            <i className="pi pi-wrench" /> Test SMS
                        </button>
                        <button
                            className={`sms-mode-btn live${smsTestForm.mode === 'live' ? ' active' : ''}`}
                            onClick={() => {
                                setSmsTestForm((p) => ({ ...p, mode: 'live' }));
                                setSmsTestResult(null);
                            }}
                        >
                            <i className="pi pi-bolt" /> Live SMS
                        </button>
                    </div>

                    {/* Mode description */}
                    <div className={`sms-mode-info ${smsTestForm.mode}`}>
                        {smsTestForm.mode === 'test' ? (
                            <>
                                <i className="pi pi-info-circle" /> Sends a <b>test message</b> to the number you enter below. Gateway logs will be captured.
                            </>
                        ) : (
                            <>
                                <i className="pi pi-exclamation-triangle" /> Sends a <b>real SMS</b> to the registered config number. This will consume gateway credits.
                            </>
                        )}
                    </div>

                    {/* Mobile input */}
                    {smsTestForm.mode === 'test' ? (
                        <div className="sub-field-group">
                            <label className="sub-field-label">
                                <i className="pi pi-phone sub-field-label-icon" /> Test Mobile Number <span className="required-mark">*</span>
                            </label>
                            <div className="sub-icon-input">
                                <i className="pi pi-phone sub-input-icon" />
                                <InputText value={smsTestForm.mobile} onChange={(e) => setSmsTestForm((p) => ({ ...p, mobile: e.target.value }))} placeholder="Enter 10-digit mobile number" className="w-full" maxLength={15} />
                            </div>
                        </div>
                    ) : (
                        <div className="sub-field-group">
                            <label className="sub-field-label">
                                <i className="pi pi-phone sub-field-label-icon" /> Registered Mobile (from config)
                            </label>
                            <div className="sub-icon-input">
                                <i className="pi pi-phone sub-input-icon" />
                                <InputText value={smsTestForm.registered_mobile || 'Not configured'} readOnly className="w-full" style={{ background: '#f3f4f6', color: '#374151' }} />
                            </div>
                        </div>
                    )}

                    {/* Message text */}
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-comment sub-field-label-icon" /> Message Text <span className="required-mark">*</span>
                        </label>
                        <InputText value={smsTestForm.message_text} onChange={(e) => setSmsTestForm((p) => ({ ...p, message_text: e.target.value }))} placeholder="Enter message to send" className="w-full" />
                    </div>

                    {/* Payload preview */}
                    <div className="sub-field-group">
                        <label className="sub-field-label" style={{ marginBottom: '0.4rem' }}>
                            <i className="pi pi-code sub-field-label-icon" /> Payload Preview
                        </label>
                        <pre style={{ background: '#1e1e2e', color: '#cdd6f4', borderRadius: '6px', padding: '0.75rem 1rem', fontSize: '0.72rem', lineHeight: 1.6, overflowX: 'auto', margin: 0, maxHeight: '140px' }}>
                            {JSON.stringify(
                                {
                                    auth_id: smsTestForm.auth_id,
                                    mode: smsTestForm.mode,
                                    mobile: smsTestForm.mode === 'live' ? smsTestForm.registered_mobile : smsTestForm.mobile,
                                    api_key: smsTestForm.api_key,
                                    entity_id: smsTestForm.entity_id,
                                    template_id: smsTestForm.template_id,
                                    user_id: smsTestForm.user_id,
                                    sender_id: smsTestForm.sender_id,
                                    message_text: smsTestForm.message_text,
                                    message_type_id: smsTestForm.message_type_id,
                                    client_id: smsTestForm.client_id,
                                    branch_id: smsTestForm.branch_id
                                },
                                null,
                                2
                            )}
                        </pre>
                    </div>

                    {/* Result panel */}
                    {smsTestResult && (
                        <div className={`sms-result-panel ${smsTestResult.ok ? 'success' : 'error'}`}>
                            <div className="sms-result-header">
                                <i className={`pi ${smsTestResult.ok ? 'pi-check-circle' : 'pi-times-circle'}`} />
                                <span>{smsTestResult.ok ? 'Delivered' : 'Failed'}</span>
                            </div>
                            <p className="sms-result-msg">{smsTestResult.message}</p>
                            {smsTestResult.gateway && <pre className="sms-result-gateway">{typeof smsTestResult.gateway === 'string' ? smsTestResult.gateway : JSON.stringify(smsTestResult.gateway, null, 2)}</pre>}
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Add / Edit Dialog */}
            <Dialog
                visible={visible}
                onHide={() => {
                    setVisible(false);
                    setFormData({});
                    setErrors({});
                }}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className={cfg.icon} />
                        </span>
                        <span className="sub-dialog-header-text">{isEditing ? `Edit ${cfg.label}` : `Add ${cfg.label}`}</span>
                    </div>
                }
                style={{ width: isSmsModule ? '700px' : '680px', maxWidth: '96vw' }}
                position="center"
                modal
                className="sub-master-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="sub-dialog-cancel"
                            onClick={() => {
                                setVisible(false);
                                setFormData({});
                                setErrors({});
                            }}
                        />
                        <Button label={isEditing ? 'Update' : 'Save'} icon={isEditing ? 'pi pi-sync' : 'pi pi-check'} className="sub-dialog-save" onClick={confirmSave} />
                    </div>
                }
            >
                <div className="sub-form-grid">{cfg.formFields.map((f) => renderField(f))}</div>
            </Dialog>
        </>
    );
};

export default Submasters;
